import * as React from 'react';
import classNames from 'classnames';
import { EmblaOptionsType } from 'embla-carousel';
import Autoplay, { AutoplayOptionsType } from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { capitalize, useEmblaArrowButton, useEmblaDotButton, useIsMobile } from '@/helpers';
import { ArrowButton as DefaultArrowButton, ArrowButtonProps } from '../ArrowButton';
import { DotButton as DefaultDotButton, DotButtonProps } from '../DotButton';
import styles from './Carousel.module.scss';

export const carouselGapSizes = ['sm', 'md', 'none'] as const;
export type CarouselGapSize = (typeof carouselGapSizes)[number];

export interface CarouselProps extends React.PropsWithChildren {
	readonly className?: string;
	readonly slidesWidth?: string | 'auto';
	readonly slideWidthMobile?: string | 'auto';
	readonly showArrows?: boolean;
	readonly showArrowsOnMobile?: boolean;
	readonly showDots?: boolean;
	readonly disableAutoplay?: boolean;
	readonly autoPlaySpeed?: number;
	readonly gap?: CarouselGapSize;
	readonly sliderLabel?: string;
	readonly ArrowButton?: React.FC<ArrowButtonProps>;
	readonly DotButton?: React.FC<DotButtonProps>;
}

/**
 * Generic Carousel component, used for showing slides of any kind of content.
 * Used e.g. in CMS context.
 */
export const Carousel: React.FC<CarouselProps> = ({
	className,
	children,
	slidesWidth = '100%',
	slideWidthMobile,
	showArrows,
	showArrowsOnMobile,
	showDots,
	disableAutoplay,
	autoPlaySpeed = 7000,
	gap = 'md',
	sliderLabel = 'Slider',
	ArrowButton = DefaultArrowButton,
	DotButton = DefaultDotButton,
}) => {
	const sliderOptions: EmblaOptionsType = {
		align: 'start',
		loop: true,
		dragThreshold: 15,
	};

	const autoplayOptions: AutoplayOptionsType = {
		playOnInit: !disableAutoplay,
		delay: autoPlaySpeed,
		stopOnMouseEnter: !disableAutoplay,
		stopOnInteraction: disableAutoplay,
	};

	const isMobile = useIsMobile();

	const autoplay = React.useRef(Autoplay(autoplayOptions));

	const [emblaRef, emblaApi] = useEmblaCarousel(sliderOptions, [autoplay.current]);

	const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = useEmblaArrowButton(emblaApi);
	const { selectedIndex, scrollSnaps, onDotButtonClick } = useEmblaDotButton(emblaApi);

	return (
		<section className={classNames(styles.wrapper, styles['gap' + capitalize(gap)], className)}>
			<div className={styles.innerWrapper}>
				{showArrows && (!isMobile || showArrowsOnMobile) && (
					<nav className={classNames(styles.arrows)} aria-label={sliderLabel}>
						<ArrowButton
							className={styles.arrow}
							onClick={onPrevButtonClick}
							disabled={prevBtnDisabled}
							prev
						/>
						<ArrowButton
							className={styles.arrow}
							onClick={onNextButtonClick}
							disabled={nextBtnDisabled}
							next
						/>
					</nav>
				)}

				<div className={styles.viewport} ref={emblaRef}>
					<ul className={styles.sliderWrapper}>
						{React.Children.map(children as never, (child: React.ReactElement, index) => (
							<li
								className={styles.slide}
								style={
									{
										'--slide-width': slidesWidth,
										'--slide-width-mobile': slideWidthMobile,
									} as React.CSSProperties
								}
								key={index}
							>
								{child}
							</li>
						))}
					</ul>
				</div>
			</div>

			{showDots && scrollSnaps.length > 1 && (
				<div className={classNames(styles.pagination, styles.isDotsType)}>
					{scrollSnaps.map((_, index) => (
						<DotButton
							key={index}
							index={index + 1}
							selected={index === selectedIndex}
							onClick={() => onDotButtonClick(index)}
						/>
					))}
				</div>
			)}
		</section>
	);
};
